import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Copy, Download, FileText, MoreVertical, Plus, Send, ShieldCheck, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { loadSentRows, runSentBatchAction, sentOutcomeClass } from "../lib/dichiarativiSent";
import { closeReservedWorkspace, openPraticaWorkspace, reservePraticaWorkspace } from "../lib/praticaWorkspace";
import { InvioPipeline } from "../components/InvioPipeline";
import { OfficialReceiptMenu } from "../components/OfficialReceiptMenu";

const DECLARATION_CODES = new Set(["REDDITI_PF", "REDDITI_SC", "REDDITI_SP", "IVA_ANNUALE", "IRAP", "CU"]);

export default function DichiarativiWorkspace() {
    const { moduleCode } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = searchParams.get("tab") === "sent" ? "sent" : "to_send";
    const code = String(moduleCode || "").toUpperCase();
    const [module, setModule] = useState(null);
    const [profiles, setProfiles] = useState([]);
    const [pratiche, setPratiche] = useState([]);
    const [sentRows, setSentRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState({});
    const [openMenuId, setOpenMenuId] = useState(null);
    const [rowBusy, setRowBusy] = useState(null);
    const [batchBusy, setBatchBusy] = useState(false);
    const [openErrorId, setOpenErrorId] = useState(null);

    const load = useCallback(async (silent = false) => {
        if (!DECLARATION_CODES.has(code)) {
            setError("Modulo dichiarativo non riconosciuto");
            setLoading(false);
            return;
        }
        if (!silent) setLoading(true);
        if (!silent) setError("");
        try {
            const [mods, profileResult, praticheResult, sentResult] = await Promise.all([
                api.get("/pratiche/modules"),
                api.get(`/dichiarativi/modules/${code}/profiles?model_year=2026`),
                api.get("/pratiche"),
                loadSentRows(api, code),
            ]);
            const found = (mods.data || []).find((item) => item.code === code);
            if (!found) throw new Error("Modulo non presente nel catalogo");
            setModule(found);
            setProfiles(profileResult.data?.profiles || []);
            setPratiche((praticheResult.data || []).filter((item) => item.module_code === code));
            setSentRows(sentResult);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message || "Impossibile caricare il modulo");
        } finally {
            if (!silent) setLoading(false);
        }
    }, [code]);

    useEffect(() => {
        load();
    }, [load]);

    const certifiedCount = useMemo(() => profiles.filter((p) => p.official_control_positive).length, [profiles]);
    const sentPraticaIds = useMemo(() => new Set(sentRows.map((item) => item.pratica_id)), [sentRows]);
    const sentStatuses = new Set(["submitted", "accepted", "rejected"]);
    const toSendPratiche = pratiche.filter((item) => {
        const sent = sentPraticaIds.has(item.id) || sentStatuses.has(item.status);
        return !sent;
    }).sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    const visibleRows = (tab === "sent" ? sentRows : toSendPratiche).slice(0, 20);
    const visibleIds = visibleRows.map((item) => tab === "sent" ? item.pratica_id : item.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected[id]);
    const selectedIds = visibleIds.filter((id) => selected[id]);
    const selectedPratiche = toSendPratiche.slice(0, 20).filter((item) => selected[item.id]);

    const toggleAllVisible = (checked) => {
        setSelected((current) => {
            const next = { ...current };
            visibleIds.forEach((id) => { next[id] = checked; });
            return next;
        });
    };

    const contributorName = (pratica) => {
        const form = pratica?.form_data || {};
        const frontespizio = form.frontespizio || form.quadri?.FRONTESPIZIO || {};
        const cognome = frontespizio.cognome || form.contribuente?.cognome || "";
        const nome = frontespizio.nome || form.contribuente?.nome || "";
        return [cognome, nome].filter(Boolean).join(" ") || "—";
    };

    const downloadReceipt = async (invioId, fmt = "RF") => {
        const invio = sentRows.find((item) => item.id === invioId);
        if (!invio?.ricevuta_disponibile) return;
        setRowBusy(invio.pratica_id);
        try {
            const result = await api.post(`/telematico/invii/${invio.id}/ricevuta-token?fmt=${fmt}`);
            const iframe = document.createElement("iframe");
            iframe.style.display = "none";
            iframe.src = result.data.url;
            document.body.appendChild(iframe);
            window.setTimeout(() => iframe.remove(), 60000);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Ricevuta non disponibile");
        } finally {
            setRowBusy(null);
        }
    };

    const duplicatePratica = async (praticaId) => {
        setRowBusy(praticaId);
        try {
            await runSentBatchAction(api, "duplicate", [praticaId]);
            toast.success("Pratica duplicata");
            setOpenMenuId(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Impossibile duplicare la pratica");
        } finally {
            setRowBusy(null);
        }
    };

    const deletePratica = async (praticaId) => {
        if (!window.confirm("Rimuovere questa pratica dall'elenco? Gli eventuali dati di invio e la ricevuta resteranno archiviati.")) return;
        setRowBusy(praticaId);
        try {
            await runSentBatchAction(api, "delete", [praticaId]);
            toast.success("Pratica rimossa");
            setOpenMenuId(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Impossibile eliminare la pratica");
        } finally {
            setRowBusy(null);
        }
    };

    const retryInvio = async (invio) => {
        if (!invio?.rinvio_disponibile) return;
        if (!window.confirm("Rinviare questa pratica all'Agenzia delle Entrate? Il sistema ripeterà controllo, autenticazione e trasmissione.")) return;
        setRowBusy(invio.pratica_id);
        try {
            await api.post(`/telematico/invii/${invio.id}/retry`);
            toast.success("Rinvio avviato");
            setOpenMenuId(null);
            await load();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.code === "REGENERATION_REQUIRED") {
                try {
                    await api.post(`/pratiche/${invio.pratica_id}/generate`);
                    await api.post("/telematico/invii", { pratica_id: invio.pratica_id });
                    toast.success("Pratica rigenerata, controllata e rinvio avviato");
                    setOpenMenuId(null);
                    await load();
                    return;
                } catch (regenerationError) {
                    const regenerationDetail = regenerationError?.response?.data?.detail;
                    toast.error(
                        typeof regenerationDetail === "string"
                            ? regenerationDetail
                            : regenerationDetail?.message || "Rigenerazione o controllo ufficiale non superato"
                    );
                    await load(true);
                    return;
                }
            }
            toast.error(
                typeof detail === "string"
                    ? detail
                    : detail?.message || "Rinvio non consentito: verifica il dettaglio dell'errore e l'esito AdE"
            );
            await load(true);
        } finally {
            setRowBusy(null);
        }
    };

    const sendPratica = async (pratica) => {
        if (!window.confirm("Inviare questa pratica all'Agenzia delle Entrate?")) return;
        setRowBusy(pratica.id);
        try {
            if (!pratica.file_generated) await api.post(`/pratiche/${pratica.id}/generate`);
            await api.post("/telematico/invii", { pratica_id: pratica.id });
            toast.success("Invio avviato");
            setOpenMenuId(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Invio non avviato: verifica i dati obbligatori e la validazione");
            await load(true);
        } finally {
            setRowBusy(null);
        }
    };

    const duplicateSelected = async () => {
        if (!selectedIds.length) return;
        if (!window.confirm(`Duplicare ${selectedIds.length} pratiche selezionate?`)) return;
        setBatchBusy(true);
        try {
            const result = await runSentBatchAction(api, "duplicate", selectedIds);
            const completed = (result.results || []).filter((item) => item.outcome === "duplicated").length;
            toast.success(`${completed} pratiche duplicate`);
            setSelected({});
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Impossibile duplicare le pratiche selezionate");
        } finally {
            setBatchBusy(false);
        }
    };

    const deleteSelected = async () => {
        if (!selectedIds.length) return;
        if (!window.confirm(`Rimuovere ${selectedIds.length} pratiche selezionate? Gli eventuali invii resteranno archiviati.`)) return;
        setBatchBusy(true);
        try {
            const result = await runSentBatchAction(api, "delete", selectedIds);
            const completed = (result.results || []).filter((item) => ["archived", "deleted"].includes(item.outcome)).length;
            toast.success(`${completed} pratiche eliminate`);
            setSelected({});
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Impossibile eliminare le pratiche selezionate");
        } finally {
            setBatchBusy(false);
        }
    };

    const sendSelected = async () => {
        if (!selectedPratiche.length) return;
        if (!window.confirm(`Inviare ${selectedPratiche.length} pratiche selezionate all'Agenzia delle Entrate?`)) return;
        setBatchBusy(true);
        let completed = 0;
        const errors = [];
        for (const pratica of selectedPratiche) {
            try {
                if (!pratica.file_generated) await api.post(`/pratiche/${pratica.id}/generate`);
                await api.post("/telematico/invii", { pratica_id: pratica.id });
                completed += 1;
            } catch (e) {
                errors.push(`${contributorName(pratica)}: ${e?.response?.data?.detail || "invio non avviato"}`);
            }
        }
        setSelected({});
        await load();
        if (completed) toast.success(`${completed} pratiche avviate all'invio`);
        if (errors.length) toast.error(`${errors.length} pratiche non inviate. ${errors.slice(0, 2).join(" · ")}`);
        setBatchBusy(false);
    };

    const createDraft = async () => {
        if (!module?.enabled) return;
        const workspaceTab = reservePraticaWorkspace(`Nuova pratica — ${module.name}`);
        setCreating(true);
        try {
            const schema = await api.get(`/pratiche/modules/${code}/schema`);
            const meta = schema.data || {};
            const annual = meta.current_schema_year || "2026";
            const formData = {
                meta: {
                    anno_modello: annual,
                    periodo_imposta: annual === "2026" ? "2025" : "",
                    specifiche_versione: "PROFILO_CERTIFICATO_DA_SELEZIONARE",
                    modulo_controllo: "DA_ESEGUIRE",
                },
            };
            const result = await api.post("/pratiche", {
                module_code: code,
                title: module.name,
                form_data: formData,
            });
            toast.success("Bozza creata");
            openPraticaWorkspace(result.data.id, workspaceTab, navigate);
        } catch (e) {
            closeReservedWorkspace(workspaceTab);
            toast.error(e?.response?.data?.detail || "Impossibile creare la bozza");
        } finally {
            setCreating(false);
        }
    };

    if (loading) return <div className="p-10 text-sm text-muted-foreground">Caricamento modulo…</div>;
    if (error) return <div className="p-10"><div className="rounded-lg border border-red-200 bg-red-50 p-5 text-red-800">{error}</div></div>;

    return (
        <div className="min-h-screen p-6 lg:p-10 space-y-6" data-testid={`declaration-workspace-${code}`}>
            <header className="rounded-xl border-2 border-orange-300 bg-white overflow-hidden">
                <div className="bg-primary px-6 py-5 text-white flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-[0.16em] text-white/80">Dichiarativi AdE · Modello 2026</div>
                        <h1 className="font-display text-3xl font-bold uppercase mt-1">{module.name}</h1>
                        <p className="text-sm mt-2 max-w-3xl text-white/90">{module.description}</p>
                    </div>
                    <button
                        type="button"
                        onClick={createDraft}
                        disabled={!module.enabled || creating}
                        className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2.5 text-sm font-bold uppercase text-orange-700 disabled:opacity-50"
                    >
                        <Plus size={15} /> {creating ? "Creazione…" : "Nuova bozza"}
                    </button>
                </div>
                {code !== "REDDITI_PF" && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-5">
                        <StatusCard icon={<ShieldCheck size={17} />} label="Profili certificati" value={`${certifiedCount}/${profiles.length}`} />
                        <StatusCard icon={<FileText size={17} />} label="Pratiche del modulo" value={String(pratiche.length)} />
                        <StatusCard
                            icon={module.telematico_ready ? <ShieldCheck size={17} /> : <AlertTriangle size={17} />}
                            label="Invio produttivo"
                            value={module.telematico_ready ? "Profili certificati" : "Non abilitato"}
                            warning={!module.telematico_ready}
                        />
                    </div>
                )}
                {!module.enabled && (
                    <div className="mx-5 mb-5 rounded-md border border-orange-300 bg-orange-50 px-4 py-3 text-sm text-orange-900">
                        Il modulo è presente ma non abilitato per questo affiliato. L’amministratore può attivarlo dal tariffario moduli.
                    </div>
                )}
            </header>

            {code !== "REDDITI_PF" && (
                <section className="rounded-xl border border-orange-300 bg-white overflow-hidden">
                    <div className="bg-primary px-5 py-3 text-white font-bold uppercase">Profili ufficiali disponibili</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 p-5">
                        {profiles.map((profile) => (
                            <article key={profile.id} className="rounded-lg border border-orange-200 p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="font-bold text-sm">{profile.id.replaceAll("_", " ").toUpperCase()}</div>
                                        <div className="text-xs text-muted-foreground mt-1 font-mono">{profile.id}</div>
                                    </div>
                                    {profile.official_control_positive && <CheckCircle2 size={18} className="text-emerald-600 shrink-0" />}
                                </div>
                                <div className="mt-3 flex flex-wrap gap-1.5">
                                    {(profile.quadri || []).map((q) => <span key={q} className="rounded bg-orange-50 border border-orange-200 px-2 py-0.5 text-[11px] font-bold">{q}</span>)}
                                </div>
                                <p className="mt-3 text-xs text-muted-foreground">
                                    {profile.production_enabled
                                        ? "Controllo ufficiale superato. Profilo abilitato alla produzione con gate fail-closed."
                                        : "Controllo ufficiale superato. Profilo non abilitato alla produzione."}
                                </p>
                            </article>
                        ))}
                        {profiles.length === 0 && <div className="text-sm text-muted-foreground">Nessun profilo pubblicato.</div>}
                    </div>
                </section>
            )}

            <section className="rounded-xl border border-orange-300 bg-white overflow-hidden">
                <div className="bg-primary px-5 py-3 text-white font-bold uppercase flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            data-testid="declaration-tab-to-send"
                            onClick={() => setSearchParams({ tab: "to_send" })}
                            className={`rounded-md px-3 py-1.5 text-xs ${tab === "to_send" ? "bg-white text-orange-700" : "bg-white/15 text-white"}`}
                        >
                            <FileText size={12} className="inline me-1" /> Da inviare
                        </button>
                        <button
                            type="button"
                            data-testid="declaration-tab-sent"
                            onClick={() => setSearchParams({ tab: "sent" })}
                            className={`rounded-md px-3 py-1.5 text-xs ${tab === "sent" ? "bg-white text-orange-700" : "bg-white/15 text-white"}`}
                        >
                            <Send size={12} className="inline me-1" /> Inviati
                        </button>
                    </div>
                    <Link to="/chat" className="inline-flex items-center gap-1.5 text-xs"><Sparkles size={13} /> Precompila dalla chat</Link>
                </div>
                {selectedIds.length > 0 && (
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-orange-200 bg-orange-50 px-5 py-3">
                        <span className="text-xs font-bold text-orange-900">{selectedIds.length} pratiche selezionate</span>
                        <div className="flex flex-wrap gap-2">
                            <button type="button" disabled={batchBusy} onClick={duplicateSelected} className="inline-flex items-center gap-1.5 rounded-md border border-orange-300 bg-white px-3 py-2 text-xs font-bold text-orange-800 hover:bg-orange-100 disabled:opacity-50"><Copy size={14} /> Duplica selezionate</button>
                            <button type="button" disabled={batchBusy} onClick={deleteSelected} className="inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-3 py-2 text-xs font-bold text-red-700 hover:bg-red-50 disabled:opacity-50"><Trash2 size={14} /> Elimina selezionate</button>
                            {tab === "to_send" && <button type="button" disabled={batchBusy} onClick={sendSelected} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-bold text-white hover:bg-orange-600 disabled:opacity-50"><Send size={14} /> Invia selezionate</button>}
                        </div>
                    </div>
                )}
                {visibleRows.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="border-b border-orange-200 bg-orange-50 text-xs font-bold uppercase text-orange-900">
                                <tr>
                                    <th className="w-12 px-4 py-3 text-center">
                                        <input type="checkbox" checked={allVisibleSelected} onChange={(e) => toggleAllVisible(e.target.checked)} aria-label="Seleziona tutte le pratiche" />
                                    </th>
                                    <th className="px-4 py-3 text-left">{tab === "sent" ? "Ragione sociale" : "Cognome e nome"}</th>
                                    <th className="px-4 py-3 text-left">{tab === "sent" ? "Data invio ↓" : "Data creazione"}</th>
                                    {tab === "sent" ? (
                                        <>
                                            <th className="px-4 py-3 text-left">Stato invio</th>
                                            <th className="px-4 py-3 text-left">Stato pratica</th>
                                            <th className="px-4 py-3 text-center">Ricevuta</th>
                                        </>
                                    ) : <th className="px-4 py-3 text-left">Stato</th>}
                                    <th className="w-16 px-4 py-3 text-center">Azioni</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-orange-100">
                                {visibleRows.map((row) => {
                                    const p = tab === "sent" ? null : row;
                                    const invio = tab === "sent" ? row : null;
                                    const praticaId = tab === "sent" ? row.pratica_id : row.id;
                                    const displayName = tab === "sent" ? (row.ragione_sociale || "—") : contributorName(row);
                                    const outcomeClass = sentOutcomeClass(invio?.stato_pratica);
                                    return (
                                    <tr key={praticaId} className="hover:bg-orange-50">
                                        <td className="px-4 py-3 text-center">
                                            <input type="checkbox" checked={Boolean(selected[praticaId])} onChange={(e) => setSelected((current) => ({ ...current, [praticaId]: e.target.checked }))} aria-label={`Seleziona ${displayName}`} />
                                        </td>
                                        <td className="px-4 py-3 font-bold">
                                            <Link to={`/pratiche/${praticaId}`} className="font-medium hover:text-orange-700">
                                                {displayName}
                                            </Link>
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3">
                                            {tab === "sent"
                                                ? (invio.data_invio ? new Date(invio.data_invio).toLocaleString("it-IT") : "—")
                                                : (p.created_at ? new Date(p.created_at).toLocaleDateString("it-IT") : "—")}
                                        </td>
                                        {tab === "sent" ? (
                                            <>
                                                <td className="px-4 py-3">
                                                    <span className={`rounded border px-2 py-1 text-[11px] font-bold uppercase ${invio.stato_invio === "FALLITO" ? "border-red-300 bg-red-50 text-red-800" : invio.stato_invio === "RIUSCITO" ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`}>{invio.stato_invio || "—"}</span>
                                                    {invio.errore_disponibile && (
                                                        <div className="mt-2">
                                                            <button type="button" onClick={() => setOpenErrorId((id) => id === invio.id ? null : invio.id)} className="inline-flex items-center gap-1 text-xs font-bold text-red-700 underline underline-offset-2">
                                                                <AlertTriangle size={13} /> {openErrorId === invio.id ? "Nascondi dettaglio" : "Dettagli errore"}
                                                            </button>
                                                            {openErrorId === invio.id && (
                                                                <div role="alert" className="mt-2 max-w-md rounded-md border border-red-200 bg-red-50 p-3 text-xs font-normal normal-case text-red-900">
                                                                    <div className="font-bold">Fase: {invio.fase_errore || "procedura telematica"}</div>
                                                                    <div className="mt-1">{invio.dettaglio_errore || "La procedura telematica non è stata completata."}</div>
                                                                    <InvioPipeline phases={invio.pipeline_fasi || []} compact />
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3"><span className={`rounded border px-2 py-1 text-[11px] font-bold uppercase ${outcomeClass}`}>{invio.stato_pratica || "—"}</span></td>
                                                <td className="px-4 py-3 text-center">
                                                    {invio?.ricevuta_disponibile ? (
                                                        <OfficialReceiptMenu invioId={invio.id} onDownload={downloadReceipt} />
                                                    ) : <span className="text-xs text-muted-foreground">—</span>}
                                                </td>
                                            </>
                                        ) : <td className="px-4 py-3"><span className="rounded border border-orange-200 px-2 py-1 text-[11px] font-bold uppercase">{p.status}</span></td>}
                                        <td className="relative px-4 py-3 text-center">
                                            <button type="button" onClick={() => setOpenMenuId((id) => id === praticaId ? null : praticaId)} disabled={rowBusy === praticaId} className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-orange-200 hover:bg-orange-100 disabled:opacity-50" aria-label={`Azioni per ${displayName}`}>
                                                <MoreVertical size={17} />
                                            </button>
                                            {openMenuId === praticaId && (
                                                <div className="absolute right-4 top-11 z-30 min-w-40 rounded-md border border-orange-200 bg-white p-1 text-left shadow-lg">
                                                    <button type="button" onClick={() => duplicatePratica(praticaId)} className="flex w-full items-center gap-2 rounded px-3 py-2 text-xs hover:bg-orange-50"><Copy size={14} /> Duplica</button>
                                                    <button type="button" onClick={() => deletePratica(praticaId)} className="flex w-full items-center gap-2 rounded px-3 py-2 text-xs text-red-700 hover:bg-red-50"><Trash2 size={14} /> Elimina</button>
                                                    {tab === "sent" && invio?.rinvio_disponibile && <button type="button" onClick={() => retryInvio(invio)} className="flex w-full items-center gap-2 rounded px-3 py-2 text-xs font-bold text-orange-700 hover:bg-orange-50"><Send size={14} /> Rinvia</button>}
                                                    {tab === "to_send" && <button type="button" onClick={() => sendPratica(p)} className="flex w-full items-center gap-2 rounded px-3 py-2 text-xs font-bold text-orange-700 hover:bg-orange-50"><Send size={14} /> Invia</button>}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );})}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="p-8 text-center text-sm text-muted-foreground">
                        {tab === "sent" ? "Nessuna pratica inviata per questo dichiarativo." : "Nessuna pratica da inviare per questo dichiarativo."}
                    </div>
                )}
            </section>
        </div>
    );
}

function StatusCard({ icon, label, value, warning = false }) {
    return (
        <div className={`rounded-lg border p-4 ${warning ? "border-orange-300 bg-orange-50" : "border-orange-200 bg-white"}`}>
            <div className="flex items-center gap-2 text-xs uppercase font-bold text-muted-foreground">{icon}{label}</div>
            <div className="mt-2 font-display text-xl font-bold">{value}</div>
        </div>
    );
}
